import {
    applyHostMeasurements,
    type EngineResult,
    type HostMeasurementResult,
    type PendingMeasurement,
    type TextBuffer,
} from '@meitu/x-design-text';
import { resolveDelegatedFontFamily } from '../font';

type DelegatedMeasureContext = {
    font: string;
    measureText(text: string): { width: number };
};

const buildDelegatedCssFont = (pending: PendingMeasurement) => {
    const weight = pending.fontWeight && pending.fontWeight !== 400 ? `${pending.fontWeight} ` : '';
    const style = pending.italic && !pending.fakeItalic ? 'italic ' : '';
    const family = resolveDelegatedFontFamily(pending.family, pending.reason);

    return `${style}${weight}${pending.fontSize}px ${family}`;
};

const createDelegatedMeasureContext = (): DelegatedMeasureContext | null => {
    if (typeof OffscreenCanvas !== 'undefined') {
        return new OffscreenCanvas(1, 1).getContext('2d');
    }

    if (typeof document !== 'undefined') {
        return document.createElement('canvas').getContext('2d');
    }

    return null;
};

const measureDelegatedAdvance = (ctx: DelegatedMeasureContext, pending: PendingMeasurement) => {
    ctx.font = buildDelegatedCssFont(pending);
    return ctx.measureText(pending.text).width;
};

/**
 * 执行文本引擎 delegated glyph 的宿主侧测量流程。
 *
 * @param buffer - TextBuffer，已经完成初次 layout 的文本缓冲区。
 * @param createContext - 可选；创建 canvas/offscreen canvas 测量上下文的工厂。
 * @returns EngineResult<HostMeasurementResult>，成功时返回本轮宿主测量统计，失败时返回引擎错误。
 */
export const measureDelegatedHostRuns = (
    buffer: TextBuffer,
    createContext: () => DelegatedMeasureContext | null = createDelegatedMeasureContext,
): EngineResult<HostMeasurementResult> => {
    let ctx: DelegatedMeasureContext | null = null;

    return applyHostMeasurements(buffer, (pending) => {
        ctx ??= createContext();
        if (!ctx) {
            throw new Error('no canvas context available for delegated measurement.');
        }

        return measureDelegatedAdvance(ctx, pending);
    });
};
